import { resolveEasing } from './easing';
import type { CssKeyframe } from './pose';
import type { MotionKey } from './schema';

/**
 * Manual key frames: a layer is placed by hand at several moments and moves between them. Moves
 * are offsets from the layer's place in the design, so the design itself never changes and the
 * presets (enter, exit, camera) keep working on top of the keyed path.
 */

export interface KeyPose {
  x: number;
  y: number;
  scale: number;
  rotate: number;
  opacity: number;
  blur: number;
}

export const REST_POSE: KeyPose = { x: 0, y: 0, scale: 1, rotate: 0, opacity: 1, blur: 0 };

/** Default curve between two keys. */
export const KEY_EASING = 'ease-in-out';

const PROPS = ['x', 'y', 'scale', 'rotate', 'opacity', 'blur'] as const;

export function sortKeys(keys: MotionKey[]): MotionKey[] {
  return [...keys].sort((a, b) => a.t - b.t);
}

/**
 * Every key with all values filled in; a value a key does not set is the rest pose (so fading in
 * is just opacity 0 on the first key, growing is scale on the last one).
 */
export function fullKeys(keys: MotionKey[]): Array<KeyPose & { t: number; easing?: string }> {
  return sortKeys(keys).map((k) => {
    const pose = { ...REST_POSE };
    for (const p of PROPS) if (k[p] !== undefined) pose[p] = k[p]!;
    return { ...pose, t: k.t, easing: k.easing };
  });
}

function bezier(x1: number, y1: number, x2: number, y2: number) {
  const at = (a: number, b: number, t: number) =>
    3 * a * t * (1 - t) * (1 - t) + 3 * b * t * t * (1 - t) + t * t * t;
  return (x: number) => {
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 30; i++) {
      const mid = (lo + hi) / 2;
      if (at(x1, x2, mid) < x) lo = mid;
      else hi = mid;
    }
    return at(y1, y2, (lo + hi) / 2);
  };
}

/** Value of a CSS timing function (as produced by resolveEasing) at progress x ∈ [0, 1]. */
export function easeAt(css: string, x: number): number {
  const v = Math.max(0, Math.min(1, x));
  if (css === 'linear') return v;
  if (css === 'ease') return bezier(0.25, 0.1, 0.25, 1)(v);
  const b = /^cubic-bezier\(([^)]+)\)$/.exec(css);
  if (b) {
    const [x1, y1, x2, y2] = b[1].split(',').map(Number);
    return bezier(x1, y1, x2, y2)(v);
  }
  const l = /^linear\(([^)]+)\)$/.exec(css);
  if (l) {
    // Evenly spaced points (that is how easing.ts samples curves)
    const pts = l[1].split(',').map(Number);
    const f = v * (pts.length - 1);
    const i = Math.min(pts.length - 2, Math.floor(f));
    return pts[i] + (pts[i + 1] - pts[i]) * (f - i);
  }
  const s = /^steps\((\d+)/.exec(css);
  if (s) {
    const n = Number(s[1]);
    return Math.min(1, Math.floor(v * n) / n);
  }
  return v;
}

/** Pose of a keyed layer at time `t` (holds the first pose before it and the last one after). */
export function keyPoseAt(keys: MotionKey[] | undefined, t: number): KeyPose {
  const list = fullKeys(keys ?? []);
  if (!list.length) return { ...REST_POSE };
  const strip = ({ t: _t, easing: _e, ...pose }: (typeof list)[number]): KeyPose => pose;
  if (t <= list[0].t) return strip(list[0]);
  const last = list[list.length - 1];
  if (t >= last.t) return strip(last);
  const i = list.findIndex((k, j) => t >= k.t && t < list[j + 1].t);
  const a = list[i];
  const b = list[i + 1];
  const e = easeAt(resolveEasing(a.easing, resolveEasing(KEY_EASING)), (t - a.t) / (b.t - a.t));
  const pose = { ...REST_POSE };
  for (const p of PROPS) pose[p] = a[p] + (b[p] - a[p]) * e;
  return pose;
}

const r = (v: number, p = 100) => Math.round(v * p) / p;

/**
 * Web Animations for keyed moves: position, size and turn as one additive animation of the
 * individual transform properties (so they stack with presets and the camera), and opacity as a
 * separate replacing animation (enter/exit fades, created later, take over while they play).
 */
export function keyAnimations(
  keys: MotionKey[] | undefined,
  baseOpacity: number,
  /** Converts the 1080-based blur to the real canvas. */
  blurScale = 1,
): Array<{
  keyframes: CssKeyframe[];
  start: number;
  duration: number;
  composite: 'add' | 'replace';
}> {
  if (!keys?.length) return [];
  const list = fullKeys(keys);
  const start = list[0].t;
  const duration = Math.max(1, list[list.length - 1].t - start);
  const has = (p: (typeof PROPS)[number]) => keys.some((k) => k[p] !== undefined);
  const offset = (t: number) => (list.length > 1 ? (t - start) / duration : 0);
  const easing = (k: (typeof list)[number]) => resolveEasing(k.easing, resolveEasing(KEY_EASING));
  const frames = (fn: (k: (typeof list)[number]) => CssKeyframe): CssKeyframe[] => {
    const out = list.map((k) => ({ ...fn(k), offset: offset(k.t), easing: easing(k) }));
    // A single key is a still pose: hold it for the whole animation
    if (out.length === 1) out.push({ ...out[0], offset: 1 });
    return out;
  };
  const out: ReturnType<typeof keyAnimations> = [];
  if (has('opacity'))
    out.push({
      keyframes: frames((k) => ({ opacity: r(k.opacity * baseOpacity, 10000) })),
      start,
      duration,
      composite: 'replace',
    });
  if (has('blur'))
    out.push({
      keyframes: frames((k) => ({ filter: `blur(${r(k.blur * blurScale)}px)` })),
      start,
      duration,
      composite: 'replace',
    });
  const move = has('x') || has('y');
  const scale = has('scale');
  const rotate = has('rotate');
  if (move || scale || rotate)
    out.push({
      keyframes: frames((k) => {
        const f: CssKeyframe = {};
        if (move) f.translate = `${r(k.x)}px ${r(k.y)}px`;
        if (scale) f.scale = String(r(k.scale, 10000));
        if (rotate) f.rotate = `${r(k.rotate)}deg`;
        return f;
      }),
      start,
      duration,
      composite: 'add',
    });
  return out;
}

/** Index of the key at time `t` (within `tolerance` ms), or -1. */
export function keyIndexAt(keys: MotionKey[] | undefined, t: number, tolerance = 20): number {
  return (keys ?? []).findIndex((k) => Math.abs(k.t - t) <= tolerance);
}

/**
 * Keys with the pose at `t` changed by `patch`: updates the key there, or inserts one holding the
 * current in-between pose so the rest of the path stays as it was.
 */
export function setKeyAt(
  keys: MotionKey[] | undefined,
  t: number,
  patch: Partial<Omit<MotionKey, 't'>>,
  tolerance = 20,
): MotionKey[] {
  const list = sortKeys(keys ?? []);
  const i = keyIndexAt(list, t, tolerance);
  if (i >= 0) {
    list[i] = { ...list[i], ...patch };
    return list;
  }
  const pose = keyPoseAt(list, t);
  const key: MotionKey = { t: Math.round(t) };
  for (const p of PROPS)
    if (patch[p] !== undefined) key[p] = patch[p];
    else if (list.some((k) => k[p] !== undefined)) key[p] = r(pose[p], 10000);
  if (patch.easing !== undefined) key.easing = patch.easing;
  return sortKeys([...list, key]);
}
